import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import useEmblaCarousel from "embla-carousel-react";
import {
  ArrowRight, CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Instagram, Pause, Play, Youtube,
} from "lucide-react";

/* ---- CONFIG ---- */
const CHECKOUT_URL = "https://checkout.luisabergholz.com/success/";
const START_DATE = "11.11.2026";

/* ---- Assets ---- */
const BANNER_SOYT = "/soyt-banner-neu.webp";
const COVER_PORTRAIT =
  "https://customer-assets-jai6qajn.emergentagent.net/job_zone-of-genius/artifacts/8ezc3s2v_SOYT%20Insta%20Beitrag.png";
const IMG_CHESS = "/gameturn-bg-neu.webp";
const LUISA_PORTRAIT =
  "https://customer-assets-jai6qajn.emergentagent.net/job_zone-of-genius/artifacts/s1az5m2s_Luisa%20provokativer.png";
const LUISA_SUNGLASSES =
  "https://customer-assets-jai6qajn.emergentagent.net/job_zone-of-genius/artifacts/93vr47x0_Luisa%20Sonnenbrille.png";
const CHESS_LANDSCAPE =
  "https://customer-assets-jai6qajn.emergentagent.net/job_zone-of-genius/artifacts/1h8fcbaf_Schach%20Backgammon%20Querformat.png";
/* Die 9 Testimonial-Screenshots (Upload 31.10., 21:42 UTC), in Upload-Reihenfolge.
   Jeder wird genau EINMAL verwendet: 1-3 über „Das alte Spiel", 4-6 über „Für Dich, wenn …",
   7-9 unter „Wer die alten Spielregeln …". */
const SHOT_1_VORGESETZTER = "/shot-vorgesetzter.png";
const SHOT_2_GEWINNEN = "/shot-gewinnen.png";
const SHOT_3_ARSCHTRITT = "/shot-arschtritt.png";
const SHOT_4_POSITIVER = "/shot-positiver.png";
const SHOT_5_ENTSCHEIDUNGEN = "/shot-entscheidungen.png";
const SHOT_6_FOKUS = "/shot-fokus.png";
const SHOT_7_GRENZEN = "/shot-grenzen.png";
const SHOT_FAMILIE =
  "https://customer-assets-jai6qajn.emergentagent.net/job_b9b4a337-83e4-4dce-9689-1a273e2b4a7e/artifacts/si6lqsfr_IMG_5699.jpeg";
const SHOT_REGELBEFOLGERIN = "/shot-regelbefolgerin.png";

/* ---- Primitives ---- */
const ease = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, y = 28, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, ease, delay }}
  >
    {children}
  </motion.div>
);

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-3 text-[11px] sm:text-xs font-body font-medium tracking-[0.34em] uppercase text-[#A39790]">
    <span className="h-px w-8 bg-gold opacity-70" />
    {children}
  </span>
);

/* ---- Anonymes Tracking (nur Zähler, keine personenbezogenen Daten) ---- */
const API = process.env.REACT_APP_BACKEND_URL;
const trackEvent = (event, page, target = "") => {
  try {
    fetch(`${API}/api/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event, page, target }),
      keepalive: true,
    });
  } catch { /* Tracking darf die Seite nie blockieren */ }
};

const CTAButton = ({ children, testid, className = "", small = false }) => (
  <a
    href={CHECKOUT_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    onClick={() => trackEvent("click", window.location.pathname, testid)}
    className={`group relative inline-flex items-center justify-center gap-3 bg-gold text-[#1a1207] font-heading font-bold uppercase tracking-wider transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:glow-gold ${
      small ? "px-6 py-3 text-xs" : "px-9 py-4 text-sm sm:text-base"
    } ${className}`}
  >
    {children}
    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
  </a>
);

const ShotFrame = ({ src, alt, testid }) => (
  <div className="w-full border border-gold bg-[#15100E] p-2">
    <img src={src} alt={alt} className="block w-full" loading="lazy" data-testid={testid} />
  </div>
);

/* ---- Spielprobe (zwischen „Das alte Spiel" und „Let's change the GAME") ---- */
const formatTime = (s) => {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

const AudioTaster = () => {
  const audioRef = useRef(null);
  const barRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); } else { a.play(); trackEvent("click", window.location.pathname, "audio-taster-play"); }
  };

  const seek = (e) => {
    const a = audioRef.current;
    const bar = barRef.current;
    if (!a || !bar || !duration) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    a.currentTime = ratio * duration;
  };

  const progress = duration ? (time / duration) * 100 : 0;

  return (
    <section id="spielprobe" className="relative scroll-mt-8 pb-6 lg:pb-10" data-testid="audio-taster-section">
      <div className="mx-auto max-w-3xl px-6">
        <Reveal>
          <div className="relative border border-gold bg-gradient-to-b from-[#231B18] to-[#15100E] p-6 sm:p-8">
            <span className="pointer-events-none absolute left-3 top-3 h-4 w-4 border-l border-t border-gold" />
            <span className="pointer-events-none absolute right-3 top-3 h-4 w-4 border-r border-t border-gold" />
            <span className="pointer-events-none absolute bottom-3 left-3 h-4 w-4 border-b border-l border-gold" />
            <span className="pointer-events-none absolute bottom-3 right-3 h-4 w-4 border-b border-r border-gold" />

            <p className="text-center text-[11px] font-body font-medium uppercase tracking-[0.34em] text-gold sm:text-xs">
              Spielprobe
            </p>
            <h2 className="mt-3 text-center font-heading text-3xl font-bold leading-tight tracking-tight text-[#F4F0EB] sm:text-4xl">
              Hör, wie es <span className="text-gold">klingt</span>.
            </h2>

            <div className="mt-6 flex items-center gap-4">
              <button
                onClick={toggle}
                data-testid="audio-taster-play"
                aria-label={playing ? "Pause" : "Abspielen"}
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gold text-[#1a1207] transition-[transform,box-shadow] duration-300 hover:scale-105 hover:glow-gold"
              >
                {playing ? <Pause className="h-6 w-6" /> : <Play className="ml-0.5 h-6 w-6" />}
              </button>

              <div className="min-w-0 flex-1">
                <div
                  ref={barRef}
                  onClick={seek}
                  data-testid="audio-taster-progress"
                  className="group h-2.5 w-full cursor-pointer rounded-full bg-[#3a2e28]"
                  role="progressbar"
                  aria-valuenow={Math.round(progress)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className="h-full rounded-full bg-gold transition-[width] duration-150"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="mt-2 flex justify-between font-body text-xs text-[#A39790]">
                  <span data-testid="audio-taster-time">{formatTime(time)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>
            </div>

            <audio
              ref={audioRef}
              preload="metadata"
              playsInline
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
              onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
              onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
            >
              <source src="/soyt-taster.m4a" type="audio/mp4" />
              <source src="/soyt-taster.mp3" type="audio/mpeg" />
            </audio>

            <p className="mt-6 text-center text-base leading-relaxed text-[#A39790] sm:text-lg">
              Das ist erst der Vorgeschmack. In der Experience warten 14 aktivierende Impulse auf Dich, die noch viel tiefer gehen.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ---- 2. Proof-Streifen direkt nach dem Hero ---- */
const ProofStrip = () => (
  <section className="relative py-10 lg:py-16" data-testid="proof-section">
    <div className="mx-auto max-w-5xl px-6 lg:px-10">
      <Reveal className="text-center">
        <Eyebrow>Stimmen aus dem Spiel</Eyebrow>
        <h3 className="mx-auto mt-5 max-w-xl font-heading text-xl font-semibold leading-snug text-[#F4F0EB] sm:text-2xl">
          Was sich bei bisherigen Teilnehmerinnen schon verändert hat
        </h3>
      </Reveal>
      <div className="mx-auto mt-8 grid max-w-4xl items-start justify-center gap-6 sm:grid-cols-2">
        <Reveal delay={0.05}>
          <div className="mx-auto w-full max-w-[440px]">
            <ShotFrame src={SHOT_1_VORGESETZTER} alt="Teilnehmerin hat berufliche Bedenken offen mit dem Vorgesetzten besprochen, statt das ganze Wochenende zu overthinken" testid="proof-shot-0" />
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="mx-auto w-full max-w-[440px]">
            <ShotFrame src={SHOT_7_GRENZEN} alt="Teilnehmerin setzt ihre Energie nur noch für Dinge ein, die sie verändern kann" testid="proof-shot-1" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---- Hero ---- */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const glowFade = useTransform(scrollYProgress, [0, 1], [1, 0]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-16 pb-20 lg:pt-20 lg:pb-24" data-testid="hero-section">
      <motion.div style={{ opacity: glowFade }} className="pointer-events-none absolute -top-64 left-1/2 hidden h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-[#FFF2B0] opacity-[0.06] blur-[120px] sm:block" />
      <div className="pointer-events-none absolute inset-0 hidden opacity-40 bg-[radial-gradient(ellipse_at_top,rgba(255,242,176,0.04),transparent_55%)] sm:block" />

      <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
        <motion.div
          className="mb-8 flex justify-center sm:hidden"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
        >
          <span className="inline-flex items-center gap-3 text-[11px] font-body font-medium tracking-[0.34em] uppercase text-[#D8CFC8]">
            <span className="h-px w-8 bg-gold opacity-70" />
            Eine 14-tägige Limonata Coaching Experience
          </span>
        </motion.div>

        <motion.p
          className="mx-auto mb-10 max-w-2xl text-center text-lg leading-relaxed text-[#F4F0EB] sm:text-xl sm:text-[#FFF2B0]"
          data-testid="hero-hook"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.05 }}
        >
          Für die Frau, bei der im Job von außen betrachtet alles super läuft.<br />Und die sich fragt, warum es sich von innen nicht so gut anfühlt.
        </motion.p>

        <motion.div
          className="relative overflow-hidden border border-gold"
          initial={{ clipPath: "inset(0 0 100% 0)", opacity: 0.4 }}
          animate={{ clipPath: "inset(0 0 0% 0)", opacity: 1 }}
          transition={{ duration: 1.2, ease, delay: 0.15 }}
        >
          <img
            src={COVER_PORTRAIT}
            alt="Success on Your Terms - Erfüllt im Job ohne Abstriche · Eine 14-tägige Limonata Coaching Experience by Luisa Bergholz"
            className="mx-auto block w-full max-w-md object-cover sm:hidden"
          />
          <img
            src={BANNER_SOYT}
            alt="Success on Your Terms - Erfüllt im Job ohne Abstriche · Eine 14-tägige Limonata Coaching Experience by Luisa Bergholz"
            className="hidden w-full object-cover sm:block"
          />
        </motion.div>

        <motion.div
          className="mx-auto mt-12 max-w-2xl text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease, delay: 0.6 }}
        >
          <p className="text-base leading-relaxed text-[#A39790] sm:text-lg">
            In dieser Experience erkennst Du, nach welchen Spielregeln Du bisher im Job spielst, und fängst an, Dein eigenes Spiel zu kreieren.
            Ohne dass Du dafür kündigen, eine Auszeit nehmen oder Abstriche machen musst.
          </p>
          <div className="mt-9 flex justify-center">
            <CTAButton testid="hero-cta">I'm in</CTAButton>
          </div>
          <p className="mt-5 text-sm text-[#A39790]" data-testid="hero-facts">
            14 aktivierende Audio-Impulse direkt auf Dein Smartphone
          </p>
          <p className="mt-2 text-sm text-[#A39790]">
            Start 11.11.26 · 11,11 € für die ersten 11 Tickets, danach 22,22 € · Zugriff bis 6.12.26
          </p>
        </motion.div>
      </div>
    </section>
  );
};

/* ---- Marquee ---- */
const Ribbon = () => (
  <div className="border-y border-gold bg-[#0A0807] py-6" data-testid="marquee">
    <Marquee speed={38} gradient gradientColor="#0A0807" gradientWidth={120}>
      {["Success on Your Terms", "Stop trying to change the rules", "Change the Game", "Success on Your Terms", "Stop trying to change the rules", "Change the Game"].map(
        (t, i) => (
          <span key={i} className="mx-10 inline-flex items-center gap-10 font-accent text-2xl italic text-[#A39790] sm:text-3xl">
            {t}
            <span className="text-gold not-italic">✦</span>
          </span>
        )
      )}
    </Marquee>
  </div>
);

/* ---- Das Problem (gekürzt) ---- */
const Zones = () => (
  <section className="relative bg-[#0A0807] py-10 lg:py-16" data-testid="zones-section">
    <div className="mx-auto max-w-3xl px-6 lg:px-10">
      <Reveal>
        <h2 className="font-heading text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
          Du bist gut in Deinem Job. Sehr gut sogar. Und genau das ist <span className="text-gold">das Problem</span>.
        </h2>
        <p className="mt-8 text-base leading-relaxed text-[#A39790] sm:text-lg">
          Du hältst alles zusammen, denkst für alle mit, lieferst ab. Und bekommst ständig
          gespiegelt, dass Du großartige Arbeit leistest. Trotzdem fühlst Du Dich innerlich leer.
        </p>
        <p className="mt-6 text-base leading-relaxed text-[#A39790] sm:text-lg">
          Aber aufgeben, was Du Dir aufgebaut hast? Vor allem, wenn Du doch offensichtlich
          so gut darin bist?
        </p>
        <div className="mt-6 space-y-1.5 border-l-2 border-gold pl-6">
          <p className="text-base italic text-[#D8CFC8] sm:text-lg">Völlig absurd, daran etwas zu ändern.</p>
          <p className="text-base italic text-[#D8CFC8] sm:text-lg">Viel zu riskant, all das aufs Spiel zu setzen.</p>
          <p className="font-heading text-xl font-semibold text-gold sm:text-2xl">Oder?</p>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---- 3. Die alten Regeln des Erfolgs ---- */
const manifestoParas = [
  { text: "Was wir von Eltern, Schule, Medien und ersten Jobs über Arbeit gelernt haben:" },
  {
    quotes: [
      "Erfolg muss ich mir hart erarbeiten.",
      "Mit dem, was Spaß macht, verdient man nicht genug.",
      "Ich muss mich in der Arbeitswelt verstellen.",
      "Sobald ich nach mehr Gehalt frage, werde ich belächelt.",
      "Das echte Leben beginnt erst nach Feierabend.",
    ],
  },
  { text: "Diese Sätze fühlen sich an wie Fakten. Sind sie aber nicht. Es sind willkürliche Spielregeln." },
  { text: "Kein Wunder, dass wir Arbeit als anstrengend und seelenlos empfinden.", bold: true },
  {
    text: <>Die meisten von uns spielen über Jahrzehnte nach diesen Regeln und verlieren sich dabei selbst. Oder sie kämpfen dagegen an und reiben sich an einem System auf, das sie nicht ändern können. <span className="font-accent italic text-gold">Beides hält Dich im selben Spiel gefangen.</span></>,
  },
  { image: true },
  { text: "Deshalb verändern noch mehr Feedbackgespräche, noch eine Weiterbildung oder eine Auszeit nichts nachhaltig. Selbst wenn es kluge Züge sind: In einem Spiel, das nicht Deins ist, kannst Du so gut spielen, wie Du willst. Es wird sich nie nach Gewinnen anfühlen." },
  { text: "Du spürst schon länger diese Sehnsucht in Dir, dass es doch auch einen anderen Weg geben muss. Denn dieses Spiel entspricht Dir nicht." },
  { text: "Und wenn es so einfach wäre, Dich hinzusetzen und groß zu träumen, hättest Du es längst getan." },
  { text: "In Success on Your Terms schauen wir deshalb hinter die Mechanismen, die Dich davon abhalten, groß zu träumen und mutige Schritte zu gehen." },
];

const Manifesto = () => (
  <section className="relative mx-auto max-w-3xl px-6 py-10 lg:py-16" data-testid="manifesto-section">
    <Reveal>
      <Eyebrow>Das alte Spiel</Eyebrow>
      <h2 className="mt-6 font-heading text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
        Die alten Regeln des <span className="text-gold">Erfolgs</span>
      </h2>
    </Reveal>

    <div className="relative mt-10 space-y-6 pl-8">
      <span className="absolute left-0 top-3 bottom-3 w-px bg-gradient-to-b from-[#EDD98F]/60 via-[#EDD98F]/15 to-transparent" />
      {manifestoParas.map((p, i) => (
        <Reveal key={i} delay={0.04}>
          {p.image ? (
            <div className="-ml-8 my-2 overflow-hidden border border-gold">
              <img
                src={CHESS_LANDSCAPE}
                alt="Eine Hand zieht eine Schachfigur auf einem Backgammon-Brett"
                className="w-full object-cover"
              />
            </div>
          ) : p.quotes ? (
            <div className="space-y-1.5 border-l-2 border-gold pl-6">
              {p.quotes.map((q, j) => (
                <p key={j} className="text-base italic text-[#D8CFC8] sm:text-lg">„{q}“</p>
              ))}
            </div>
          ) : (
            <div className="relative">
              <span className="absolute -left-8 top-2.5 h-2 w-2 -translate-x-1/2 rounded-full bg-[#FFF2B0]" />
              {p.gold ? (
                <p className="font-accent text-xl italic leading-relaxed text-gold sm:text-2xl">{p.text}</p>
              ) : (
                <p
                  className={`leading-relaxed ${
                    p.lead
                      ? "text-xl text-[#F4F0EB] sm:text-2xl"
                      : p.bold
                      ? "text-base font-semibold text-[#F4F0EB] sm:text-lg"
                      : "text-base text-[#A39790] sm:text-lg"
                  }`}
                >
                  {p.text}
                </p>
              )}
            </div>
          )}
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---- 4. Let's change the GAME (zusammengelegt) ---- */
const GameTurn = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="game-section">
      <motion.img style={{ y }} src={IMG_CHESS} alt="" className="absolute inset-0 h-[125%] w-full object-cover opacity-95" />
      <div className="absolute inset-0 bg-[#0A0807]/30" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#15100E] via-transparent to-[#15100E]" />

      <div className="relative mx-auto max-w-4xl px-6 py-12 lg:py-16">
        <Reveal>
          <p className="font-accent text-xl italic text-[#A39790] sm:text-2xl">
            Anstatt zu versuchen, die Spielregeln zu verändern …
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <h2 className="mt-8 font-heading text-5xl font-black leading-[0.95] tracking-tight sm:text-7xl lg:text-8xl">
            <span className="text-[#F4F0EB]">Let's change</span>
            <br />
            <span className="text-gold">the GAME.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.3}>
          <p className="mx-auto mt-8 max-w-2xl font-heading text-3xl font-bold leading-tight text-gold sm:text-4xl" data-testid="gameturn-gold-statement">
            Es geht nicht darum, das alte Spiel <span className="italic">besser</span> zu spielen. Es geht darum, Dein eigenes Spiel zu kreieren.
          </p>
          <p className="mx-auto mt-8 max-w-xl text-base leading-relaxed text-[#A39790] sm:text-lg">
            Und nein: Das Spielfeld zu verlassen heißt nicht, zu kündigen. Es passiert
            zuerst im Kopf. Du erkennst, nach welchen Regeln Du bisher gespielt hast,
            und entscheidest neu. <span className="font-semibold">Du musst nicht erst einen neuen Job haben, um so zu arbeiten, wie es Dir entspricht.</span> Der
            Job kann formal derselbe bleiben, und Du erlebst ihn trotzdem ganz anders.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

/* ---- 5. Für Dich, wenn … + Am Ende ---- */
const workDesires = [
  <>die nicht nur von außen großartig aussieht, sondern sich <strong className="font-semibold">von innen noch viel besser anfühlt</strong>.</>,
  <>die Dir und Deinem Wesen entspricht und Dir hilft, <strong className="font-semibold">immer wieder über Dich hinauszuwachsen</strong>.</>,
  <>in der Du <strong className="font-semibold">echten Mehrwert schaffst</strong>, statt Stunden abzusitzen, und Deine Energietanks <strong className="font-semibold">aufgeladen</strong> werden, statt leergesaugt zu werden.</>,
  <>in der Du nicht mehr gegen „das Management" oder „die Kunden" kämpfst, sondern <strong className="font-semibold">echte Beziehungen von Mensch zu Mensch</strong> lebst, unabhängig von der Rolle.</>,
  <>in der Du Deinen Arbeitstag so gestaltest, wie er zu Dir passt: <strong className="font-semibold">Zeiten, Ort, Tempo</strong>.</>,
];
const endGains = [
  "Erkannt, woher Deine Spielregeln kommen (Eltern, Schule, erste Jobs), und welche davon wirklich Deine sind.",
  "Durchschaut, mit welchen vernünftig klingenden Gründen Du Dich bisher klein gehalten hast.",
  "Mindestens einen mutigen Schritt gemacht, den Du schon lange vor Dir hergeschoben hast, und Dich dabei als Main Character Deines Lebens erlebt.",
  "Neue Standards gesetzt: Du weißt, wofür Du ab sofort zur Verfügung stehst.",
  "Ein Umfeld gefunden, das sich gegenseitig feiert, spiegelt und herausfordert, statt in Zweifeln zu bestärken.",
];

const NewGame = () => (
  <section className="relative py-10 lg:py-16" data-testid="newgame-section">
    <div className="mx-auto max-w-3xl px-6">
      <Reveal>
        <h2 className="font-heading text-2xl font-semibold leading-snug text-[#F4F0EB] sm:text-3xl" data-testid="fuer-dich-headline">
          Success on Your Terms ist für Dich, wenn Du eine Arbeit möchtest …
        </h2>
        <ul className="mt-6 space-y-5">
          {workDesires.map((t, i) => (
            <li key={i} className="flex gap-4 text-base leading-relaxed text-[#A39790] sm:text-lg">
              <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#FFF2B0]" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <p className="mt-6 text-base leading-relaxed text-[#A39790] sm:text-lg" data-testid="fuer-dich-anstellt-selbststaendig">
          Egal, ob angestellt oder selbstständig.
        </p>
      </Reveal>

      <hr className="mt-10 border-0 border-t border-gold opacity-30" />

      <Reveal delay={0.08}>
        <p className="mt-10 font-accent text-3xl italic text-gold sm:text-4xl">Am Ende von Success on Your Terms</p>
        <p className="mt-6 text-xl leading-relaxed text-[#F4F0EB] sm:text-2xl">
          blickst Du auf die Frau zurück, die dieses Spiel begonnen hat, und wirst sie kaum wiedererkennen.
        </p>
        <p className="mt-6 text-base leading-relaxed text-[#A39790] sm:text-lg">
          Die Frau, die nach dem „naja, so ist Arbeit halt"-Autopiloten gelebt hat. Die das ungute Bauchgefühl mit „Aber eigentlich ist doch alles gut" wegerklärt hat. Die darauf gewartet hat, dass jemand kommt und ihr die Erlaubnis gibt.
        </p>
        <p className="mt-4 font-heading text-xl font-semibold text-[#F4F0EB] sm:text-2xl">Diese Frau gibt es nicht mehr.</p>

        <p className="mt-8 text-base font-semibold text-[#F4F0EB] sm:text-lg">Stattdessen hast Du:</p>
        <ul className="mt-6 space-y-5">
          {endGains.map((g, i) => (
            <li key={i} className="flex gap-4 text-base leading-relaxed text-[#D8CFC8] sm:text-lg">
              <span className="mt-0.5 shrink-0">🦸‍♀️</span>
              <span>{g}</span>
            </li>
          ))}
        </ul>

        <div className="mt-8 border-l-2 border-gold pl-6">
          <p className="font-accent text-xl italic leading-relaxed text-gold sm:text-2xl">
            Wer die alten Spielregeln einmal durchschaut hat, kann sie nicht mehr nicht sehen.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---- 6. Pull-Quote + Carousel mit Teilnehmerinnen-Stimmen ---- */
const voiceShots = [
  { src: SHOT_2_GEWINNEN, alt: "Teilnehmerin wusste nicht genau, was sie sich von SOYT vorstellen kann - und hat viel mehr bekommen, als sie sich hätte vorstellen können", testid: "voice-shot-0" },
  { src: SHOT_4_POSITIVER, alt: "Teilnehmerin ist viel positiv gestimmter und mehr im Vertrauen, dass sich alles zu ihrem Besten regeln wird", testid: "voice-shot-1" },
  { src: SHOT_5_ENTSCHEIDUNGEN, alt: "Teilnehmerin trifft mittlerweile oft leichter Entscheidungen und hat ihren ersten großen Main Character Move gemacht", testid: "voice-shot-2" },
  { src: SHOT_6_FOKUS, alt: "Teilnehmerin hat dank Success on Your Terms ihren Fokus wiedergefunden und sieht ihren Weg wieder klar vor sich", testid: "voice-shot-3" },
  { src: SHOT_3_ARSCHTRITT, alt: "SOYT war nach Selbstneuerfinderin nochmal ein Arschtritt, dranzubleiben und groß zu träumen", testid: "voice-shot-4" },
  { src: SHOT_REGELBEFOLGERIN, alt: "Es tut wirklich gut, sich die Rolle der Regel-Befolgerin abzutrainieren, weil man sich sonst den Stress nur selber macht", testid: "voice-shot-5" },
];

const VoiceCarousel = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", slidesToScroll: 1 });
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState([]);
  const onSelect = useCallback((api) => setSelected(api.selectedScrollSnap()), []);

  useEffect(() => {
    if (!emblaApi) return;
    setSnaps(emblaApi.scrollSnapList());
    onSelect(emblaApi);
    emblaApi.on("select", onSelect).on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  return (
    <section className="relative py-8 lg:py-12" data-testid="voices-section">
      <div className="mx-auto max-w-5xl px-6 lg:px-10">
        <Reveal>
          <p className="mx-auto max-w-[820px] text-center font-accent text-[26px] font-light italic leading-[1.3] text-[#F4F0EB] sm:text-[30px] lg:text-[clamp(28px,4vw,44px)]" data-testid="voices-pullquote">
            „Es macht einen Unterschied, ob ich spiele, um nicht zu verlieren, oder ob ich spiele, um zu gewinnen. <span className="text-gold">Ich will gewinnen!</span>"
          </p>
          <p className="mt-5 text-center text-[11px] font-body font-medium uppercase tracking-[0.34em] text-[#A39790] sm:text-xs">
            Mitspielerin · SOYT
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8 overflow-hidden" ref={emblaRef} data-testid="voices-carousel">
            <div className="flex gap-6">
              {voiceShots.map((s, i) => (
                <div key={i} className="min-w-0 flex-[0_0_100%] sm:flex-[0_0_calc(50%-12px)]">
                  <ShotFrame src={s.src} alt={s.alt} testid={s.testid} />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between">
            <div className="flex gap-2">
              {snaps.map((_, i) => (
                <button
                  key={i}
                  data-testid={`voice-dot-${i}`}
                  onClick={() => emblaApi && emblaApi.scrollTo(i)}
                  aria-label={`Stimme ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    selected === i ? "w-8 bg-gold" : "w-3 bg-[#3a2e28]"
                  }`}
                />
              ))}
            </div>
            <div className="flex gap-3">
              <button
                data-testid="voice-prev"
                onClick={() => emblaApi && emblaApi.scrollPrev()}
                aria-label="Zurück"
                className="flex h-11 w-11 items-center justify-center border border-gold text-[#F4F0EB] transition-colors hover:bg-gold hover:text-[#1a1207]"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                data-testid="voice-next"
                onClick={() => emblaApi && emblaApi.scrollNext()}
                aria-label="Weiter"
                className="flex h-11 w-11 items-center justify-center border border-gold text-[#F4F0EB] transition-colors hover:bg-gold hover:text-[#1a1207]"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ---- 7. „Success on Your Terms heißt …" + Frage + Button ---- */
const HeisstCta = () => (
  <section className="relative py-10 lg:py-16" data-testid="heisst-section">
    <div className="mx-auto max-w-3xl px-6">
      <Reveal>
        <div className="text-center">
          <p className="font-accent text-xl italic leading-relaxed text-gold sm:text-2xl">
            Success on Your Terms heißt: Tun, was Du liebst. UND richtig gut dafür bezahlt
            werden. UND wirklich Mehrwert für andere schaffen.
          </p>
          <h3 className="mt-8 font-heading text-2xl font-semibold leading-snug text-[#F4F0EB] sm:text-3xl" data-testid="heisst-question">
            Was, wenn Du 14 Tage lang alles vergisst, was Du bisher über Arbeit zu wissen glaubst, und zum ersten Mal Dein eigenes Spiel spielst?
          </h3>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---- 6. Stimmen aus dem Spiel (nur iframe) ---- */

/* ---- 7. Was Dich erwartet ---- */
const gameFacts = [
  { label: "Spielstart", value: "11. November 2026, Einstieg auch später möglich" },
  { label: "Spielformat", value: "14 aktivierende Audio-Impulse, die Deine Perspektive auf Dich und auf Arbeit komplett verändern werden." },
  { label: "Spielzüge", value: "Zu jedem Impuls gibt's Reflexionsfragen oder kleine Übungen, die Du direkt in Deinem Alltag umsetzt." },
  { label: "Spielort", value: "Direkt auf Dein Smartphone via Telegram, zeitversetzt auch im Mitgliederbereich. Zum Hören beim Autofahren, Kochen oder Spazieren." },
  { label: "Spieldauer", value: "14 Tage. Zugriff auf alle Inhalte bis einschließlich 6.12.26." },
  { label: "Mitspielerinnen", value: "Eine Community von Frauen, die sich gegenseitig feiert, spiegelt und herausfordert. Ko-Kreation statt Konkurrenz: Wir gewinnen gemeinsam, oder gar nicht." },
  { label: "Spielregeln", value: "Noch weitgehend unbekannt. Genau das macht es spannend. Nur so viel: Es gibt die ein oder andere Karte, die ich noch nicht aufdecke. 🃏" },
  { label: "Spielpreis", value: "11,11 € für die ersten 11 Tickets, danach steigt der Preis stufenweise auf 39 €." },
];

const Included = () => (
  <section className="mx-auto max-w-3xl px-6 py-10 lg:py-16" data-testid="included-section">
    <Reveal>
      <Eyebrow>Was Dich erwartet</Eyebrow>
      <h2 className="mt-6 font-heading text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
        Die Spiele werden <span className="text-gold">am 11.11.2026</span> eröffnet.
      </h2>
    </Reveal>

    <Reveal delay={0.12}>
      <div className="relative mt-8 border border-gold bg-gradient-to-b from-[#231B18] to-[#15100E] p-8 sm:p-12">
        <span className="pointer-events-none absolute left-3 top-3 h-4 w-4 border-l border-t border-gold" />
        <span className="pointer-events-none absolute right-3 top-3 h-4 w-4 border-r border-t border-gold" />
        <span className="pointer-events-none absolute bottom-3 left-3 h-4 w-4 border-b border-l border-gold" />
        <span className="pointer-events-none absolute bottom-3 right-3 h-4 w-4 border-b border-r border-gold" />

        <p className="text-center font-heading text-xs uppercase tracking-[0.4em] text-gold">Spielanleitung</p>
        <div className="mx-auto mt-3 h-px w-16 bg-gold opacity-40" />

        <dl className="mt-8 divide-y divide-[rgba(255,242,176,0.12)]">
          {gameFacts.map((f) => (
            <div key={f.label} className="flex flex-col gap-1.5 py-5 sm:flex-row sm:items-baseline sm:gap-8">
              <dt className="shrink-0 font-heading text-xs uppercase tracking-[0.28em] text-gold sm:w-44">{f.label}</dt>
              <dd className="text-lg leading-relaxed text-[#F4F0EB]">{f.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </Reveal>

    <Reveal delay={0.16}>
      <p className="mt-8 text-center font-accent text-lg italic text-[#756A63]">
        Mehr Details entfalten sich im Spielprozess.
      </p>
    </Reveal>
  </section>
);

/* ---- Kurz beantwortet + Nicht für Dich (ganz unten) ---- */
const faqs = [
  {
    q: "Muss ich direkt kündigen?",
    a: "Nein. Das Spielfeld zu verlassen passiert zuerst im Kopf. Ich selbst bin nach dieser Entscheidung noch zwei Jahre in meinem Angestelltenjob geblieben und habe diesen viel mehr zu meinem Job gemacht. Er hat sich völlig anders angefühlt.",
  },
  {
    q: "Wie viel Zeit brauche ich dafür?",
    a: "Die Impulse dauern in der Regel zwischen 20 und 40 Minuten. Sie sind bewusst im Audioformat gestaltet, damit Du sie hören kannst, während Du Dinge tust, die Du sowieso tust: beim Autofahren, Kochen, Wäsche aufhängen oder bei der Einschlafbegleitung. Du hast Zugriff bis 6.12.26 und kannst also auch später einsteigen oder Dir mehr Zeit lassen.",
  },
  {
    q: "Was, wenn ich kein Telegram nutze?",
    a: "Die App ist kostenlos, und Du kannst den Kanal auch im Browser am Laptop öffnen. Alternativ gibt es die Impulse zeitversetzt im Mitgliederbereich, dort allerdings ohne Austausch mit den anderen.",
  },
];

const KurzBeantwortet = () => (
  <section className="mx-auto max-w-3xl px-6 py-10 lg:py-16" data-testid="kurz-beantwortet-section">
    <Reveal>
      <div data-testid="faq-section">
        <Eyebrow>FAQ</Eyebrow>
        <div className="mt-8 divide-y divide-[rgba(255,242,176,0.12)]">
          {faqs.map((f, i) => (
            <details key={i} className="group py-6" data-testid={`faq-item-${i}`}>
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 [&::-webkit-details-marker]:hidden">
                <span className="font-heading text-xl font-semibold text-[#F4F0EB] sm:text-2xl">{f.q}</span>
                <ChevronDown className="h-5 w-5 shrink-0 text-gold transition-transform duration-300 group-open:rotate-180" />
              </summary>
              <p className="mt-4 text-base leading-relaxed text-[#A39790] sm:text-lg">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </Reveal>
  </section>
);

/* ---- „Nicht für Dich, wenn …": gewichtiger Abschlussblock ---- */
const NichtFuerDich = () => (
  <section className="mx-auto max-w-3xl px-6 pb-14 pt-10 lg:pb-16 lg:pt-16" data-testid="nicht-fuer-dich-section">
    <Reveal>
      <Eyebrow>Für wen nicht</Eyebrow>
      <h2 className="mt-6 font-heading text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
        Success on Your Terms ist <span className="text-gold">nicht</span> für Dich, wenn …
      </h2>
    </Reveal>

    <Reveal delay={0.1}>
      <div className="relative mt-8 border border-gold bg-gradient-to-b from-[#231B18] to-[#15100E] p-8 sm:p-12">
        <span className="pointer-events-none absolute left-3 top-3 h-4 w-4 border-l border-t border-gold" />
        <span className="pointer-events-none absolute right-3 top-3 h-4 w-4 border-r border-t border-gold" />
        <span className="pointer-events-none absolute bottom-3 left-3 h-4 w-4 border-b border-l border-gold" />
        <span className="pointer-events-none absolute bottom-3 right-3 h-4 w-4 border-b border-r border-gold" />
        <ul className="space-y-5">
          <li className="flex gap-4 text-base leading-relaxed text-[#D8CFC8] sm:text-lg">
            <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#FFF2B0]" />
            <span>Du jemanden suchst, der Dich rettet oder Dir sagt, was Du tun sollst.</span>
          </li>
          <li className="flex gap-4 text-base leading-relaxed text-[#D8CFC8] sm:text-lg">
            <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#FFF2B0]" />
            <span>Du vor allem darin bestärkt werden willst, dass alles schwierig ist und nichts geht.</span>
          </li>
        </ul>
      </div>
    </Reveal>

    <Reveal delay={0.15}>
      <p className="mt-8 text-center text-base leading-relaxed text-[#A39790] sm:text-lg">
        Alle Gefühle und Zweifel sind hier erlaubt. Aber Du kommst, um etwas zu verändern.
      </p>
      <p className="mt-8 text-center font-heading text-3xl font-bold leading-snug text-[#F4F0EB] sm:text-4xl">
        Du brauchst das hier nicht. Du hast bisher auch ohne ganz gut überlebt.
        <span className="mt-3 block">Die Frage ist: <span className="text-gold">Willst Du es?</span></span>
      </p>
      <div className="mt-8 flex justify-center">
        <CTAButton testid="nicht-fuer-dich-cta">I'm in</CTAButton>
      </div>

      <div className="mt-12">
        <p className="text-center text-base leading-relaxed text-[#A39790] sm:text-lg">
          Achtung - wenn Du hier mitmachst, kann sich das auch auf andere Lebensbereiche auswirken, wie bei dieser Teilnehmerin:
        </p>
        <div className="mx-auto mt-6 max-w-[240px] sm:max-w-[280px]">
          <ShotFrame src={SHOT_FAMILIE} alt="Teilnehmerin hat sich neu in ihren Mann verliebt und setzt den Fokus voll auf das, was sie möchte" testid="achtung-shot" />
        </div>
      </div>
    </Reveal>
  </section>
);

/* ---- 8. Brand band ---- */
const BrandBand = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section ref={ref} className="relative overflow-hidden bg-[#0A0807]" data-testid="brand-band">
      {/* Mobile / tablet: full-bleed band */}
      <div className="relative h-[420px] overflow-hidden sm:h-[520px] lg:hidden">
        <motion.img style={{ y }} src={LUISA_SUNGLASSES} alt="Luisa Bergholz" className="absolute inset-0 h-[116%] w-full object-cover object-[center_25%]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0807] via-[#0A0807]/20 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0807]/70 via-transparent to-transparent" />
        <div className="relative mx-auto flex h-full max-w-7xl items-center px-6">
          <Reveal>
            <p className="max-w-md font-accent text-2xl italic leading-snug text-[#F4F0EB] sm:text-4xl">
              Es ist kein Erfolg, wenn Du dafür andere Bereiche Deines Lebens opfern musst.
            </p>
            <p className="mt-4 font-heading text-lg font-semibold uppercase tracking-widest text-gold">
              Success on Your Terms. Ohne Abstriche.
            </p>
          </Reveal>
        </div>
      </div>

      {/* Desktop: contained split (crisp, non-upscaled image) */}
      <div className="mx-auto hidden max-w-7xl items-stretch gap-12 px-10 py-14 lg:flex">
        <div className="flex flex-1 items-center">
          <Reveal>
            <p className="max-w-lg font-accent text-4xl italic leading-snug text-[#F4F0EB]">
              Es ist kein Erfolg, wenn Du dafür andere Bereiche Deines Lebens opfern musst.
            </p>
            <p className="mt-5 font-heading text-lg font-semibold uppercase tracking-widest text-gold">
              Success on Your Terms. Ohne Abstriche.
            </p>
          </Reveal>
        </div>
        <div className="relative w-[380px] shrink-0 overflow-hidden border border-gold">
          <img src={LUISA_SUNGLASSES} alt="Luisa Bergholz" className="h-[480px] w-full object-cover object-[center_25%]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0807]/40 to-transparent" />
        </div>
      </div>
    </section>
  );
};

/* ---- 9. Buchen ---- */
// Preisstaffelung: vergriffene Stufen einfach aus priceTiers entfernen.
const priceTiers = [
  { label: "Die ersten 11 Tickets kosten 11,11 €" },
  { label: "Die nächsten 22: 22,22 €" },
  { label: "Die nächsten 33: 33,33 €" },
  { label: "Danach: regulär 39,00 €" },
];

const Pricing = () => (
  <section className="mx-auto max-w-3xl px-6 py-10 lg:py-16" data-testid="pricing-section">
    <Reveal>
      <div className="relative overflow-hidden border border-gold bg-gradient-to-b from-[#231B18] to-[#0A0807] p-10 text-center sm:p-14">
        <div className="pointer-events-none absolute -top-20 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-[#E5C158] opacity-10 blur-[90px]" />
        <span className="inline-flex items-center gap-2 border border-gold px-4 py-1.5 text-[11px] font-body font-medium uppercase tracking-[0.28em] text-[#A39790]">
          <CalendarDays className="h-3.5 w-3.5 text-gold" /> Start {START_DATE}
        </span>

        <h2 className="mt-8 font-heading text-3xl font-bold sm:text-4xl">Buch Dir hier Deinen Platz</h2>

        <div className="mt-8 flex items-end justify-center gap-4">
          <span className="text-sm uppercase tracking-widest text-[#A39790]">Dein Invest</span>
          <span className="font-heading text-2xl font-bold text-[#756A63] line-through sm:text-3xl">39 €</span>
          <span className="font-heading text-6xl font-black text-gold sm:text-7xl">11,11 €</span>
        </div>
        <p className="mt-3 font-body text-sm text-[#A39790]">
          für die ersten 11 Tickets · Einmalzahlung, inkl. MwSt.
        </p>

        <p className="mt-8 text-base leading-relaxed text-[#A39790] sm:text-lg">
          Aber weil wir schon bei der Buchung spielen, gibt es Startplätze zu Spezialpreisen:
        </p>
        <ul className="mx-auto mt-6 max-w-md space-y-3 text-left">
          {priceTiers.map((t, i) => (
            <li key={i} className="flex gap-3 text-base leading-relaxed text-[#D8CFC8] sm:text-lg" data-testid={`price-tier-${i}`}>
              <span className="mt-0.5 shrink-0">🃏</span>
              <span>{t.label}</span>
            </li>
          ))}
        </ul>
        <p className="mx-auto mt-6 max-w-md text-left text-base leading-relaxed text-[#A39790] sm:text-lg">
          Du bekommst bei jedem Ticket dieselbe Experience, deren Wert um ein Vielfaches höher ist als der reguläre Preis.
        </p>

        <div className="my-8 h-px w-full bg-gold opacity-30" />

        <div>
          <CTAButton testid="pricing-cta" className="w-full sm:w-auto">I'm in</CTAButton>
        </div>
      </div>
    </Reveal>
  </section>
);

/* ---- 10. Deine Spielleiterin ---- */
const Coach = () => (
  <section className="mx-auto max-w-7xl px-6 py-14 lg:px-10 lg:py-24" data-testid="coach-section">
    <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
      <Reveal>
        <div className="relative mx-auto max-w-sm overflow-hidden arch border border-gold">
          <img src={LUISA_PORTRAIT} alt="Luisa Bergholz" className="h-[540px] w-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#15100E] to-transparent" />
        </div>
      </Reveal>
      <Reveal delay={0.12}>
        <Eyebrow>Deine Spielleiterin</Eyebrow>
        <h2 className="mt-6 font-heading text-4xl font-bold tracking-tight sm:text-5xl">
          Luisa <span className="text-gold">Bergholz</span>
        </h2>
        <p className="mt-5 text-base leading-relaxed text-[#A39790] sm:text-lg">
          Arbeitspsychologin, Expertin für berufliche Neuorientierung, Projektorin, Dolce-Vita-Genießerin.
        </p>
        <p className="mt-6 font-accent text-lg italic leading-relaxed text-gold sm:text-xl">
          Ich glaube nicht, dass wir uns zwischen Erfolg und Erfüllung entscheiden
          müssen.
        </p>
        <p className="mt-4 text-base leading-relaxed text-[#A39790] sm:text-lg">
          Ich habe selbst lange nach den alten Regeln gespielt, war Meisterin darin,
          sie brav zu befolgen - in meinem Job als angestellte Personalberaterin und
          sogar als Selbstständige noch. Bis ich anfing, mich zu fragen: Wer hat
          eigentlich gesagt, dass Arbeit so aussehen muss?
        </p>
        <p className="mt-6 font-accent text-lg italic leading-relaxed text-gold sm:text-xl">
          Dieser Job wird mein Spielfeld.
        </p>
        <p className="mt-4 text-base leading-relaxed text-[#A39790] sm:text-lg">
          Ich habe ihn so gestaltet, wie ich es für richtig hielt, und er hat sich
          völlig anders angefühlt. In diesem neu gestalteten Job, der formal fast
          gleich blieb, bin ich noch zwei Jahre geblieben.
        </p>
        <p className="mt-4 text-base leading-relaxed text-[#A39790] sm:text-lg">
          Inzwischen lebe ich ein Leben, in dem sich meine Arbeit auf natürliche
          Weise einfügt. Genieße die Arbeit mit meinen Kundinnen genauso sehr wie
          ein Treffen mit Freundinnen. Und halte immer wieder in mittlerweile ganz
          „normalen" Alltagsmomenten inne und blicke mich staunend um, dass DAS
          tatsächlich mein ArbeitsLeben ist.
        </p>
        <p className="mt-4 text-base leading-relaxed text-[#A39790] sm:text-lg">
          „Success on Your Terms" katapultiert Dich aus dem alten Spiel, hin zu einer
          Arbeit, die sich wirklich nach Dir anfühlt.
        </p>
        <p className="mt-6 font-accent text-2xl italic text-gold sm:text-3xl">
          Are you ready to Change the Game?
        </p>
      </Reveal>
    </div>
  </section>
);

/* ---- Footer ---- */
const Footer = () => (
  <footer className="border-t border-gold bg-[#0A0807] py-12" data-testid="footer">
    <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 lg:flex-row lg:items-center lg:px-10">
      <div className="flex items-center gap-5">
        <span className="font-body text-sm text-[#756A63]">by Luisa Bergholz</span>
        <div className="flex gap-4">
          <a href="https://www.instagram.com/luisa.bergholz/" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="text-[#A39790] transition-colors hover:text-gold">
            <Instagram className="h-5 w-5" />
          </a>
          <a href="https://www.youtube.com/@luisa.bergholz" target="_blank" rel="noopener noreferrer" data-testid="footer-youtube" aria-label="YouTube" className="text-[#A39790] transition-colors hover:text-gold">
            <Youtube className="h-5 w-5" />
          </a>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-7 gap-y-3 font-body text-sm text-[#A39790]">
        <a href="https://luisabergholz.com/impressum" target="_blank" rel="noopener noreferrer" data-testid="footer-impressum" className="transition-colors hover:text-gold">Impressum</a>
        <a href="https://luisabergholz.com/datenschutzerklaerung" target="_blank" rel="noopener noreferrer" data-testid="footer-datenschutz" className="transition-colors hover:text-gold">Datenschutz</a>
        <a href="https://luisabergholz.com/agb" target="_blank" rel="noopener noreferrer" data-testid="footer-agb" className="transition-colors hover:text-gold">AGB</a>
        <a href="https://luisabergholz.com/widerrufsformular" target="_blank" rel="noopener noreferrer" data-testid="footer-widerruf" className="transition-colors hover:text-gold">Vertrag widerrufen</a>
        <a href="https://luisabergholz.com/kontakt" target="_blank" rel="noopener noreferrer" data-testid="footer-kontakt" className="transition-colors hover:text-gold">Kontakt</a>
      </div>
    </div>
    <p className="mx-auto mt-8 max-w-7xl px-6 font-body text-xs text-[#756A63] lg:px-10">
      © {new Date().getFullYear()} Luisa Bergholz. Alle Rechte vorbehalten.
    </p>
  </footer>
);

export default function SalesPage() {
  const { pathname } = useLocation();
  const trackedRef = useRef(false);

  // Anonymes Pageview-Tracking (einmal pro Seitenaufruf)
  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    trackEvent("pageview", pathname);
  }, [pathname]);

  // Anker-Support: bei URL mit #spielprobe nach dem Rendern dorthin scrollen (Lenis-kompatibel)
  useEffect(() => {
    if (window.location.hash !== "#spielprobe") return;
    const t = setTimeout(() => {
      const el = document.getElementById("spielprobe");
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 40;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 600);
    return () => clearTimeout(t);
  }, []);

  return (
    <main className="relative min-h-screen bg-[#15100E]">
      <Hero />
      <ProofStrip />
      <Ribbon />
      <Zones />
      <Manifesto />
      <AudioTaster />
      <GameTurn />
      <NewGame />
      <VoiceCarousel />
      <HeisstCta />
      <Included />
      <BrandBand />
      <Pricing />
      <Coach />
      <KurzBeantwortet />
      <NichtFuerDich />
      <Footer />
    </main>
  );
}
